import React           from 'react';
import useDesignTokens from '@/hooks/useDesignTokens';

const mix = (color: string, percent: number, base: string) => `color-mix(in srgb, ${color} ${percent}%, ${base})`;

const ROADS = ['M-10 70 L330 95', 'M110 -10 L135 210', 'M230 -10 C215 60 250 120 240 210'];

const BUILDINGS: [number, number, number, number][] = [
	[20, 16, 34, 26],
	[62, 12, 30, 34],
	[150, 14, 56, 40],
	[258, 20, 44, 30],
	[20, 96, 74, 34],
	[262, 112, 40, 28],
];

export function LocatedMapIllustration() {
	const tokens = useDesignTokens();
	const base = tokens.surface.card;

	return (
		<svg viewBox="0 14 320 172" width="100%" style={{ display: 'block', aspectRatio: '320 / 172' }} aria-hidden>
			<rect width="320" height="200" fill={mix(tokens.color.success, 12, base)} />
			<path d="M0 150 C60 135 90 175 150 165 S260 140 320 160 V200 H0z" fill={mix(tokens.color.primary, 22, base)} />

			<g stroke={tokens.border.default} strokeWidth={13} fill="none" strokeLinecap="round">
				{ROADS.map(d => <path key={d} d={d} />)}
			</g>

			<g stroke={base} strokeWidth={10} fill="none" strokeLinecap="round">
				{ROADS.map(d => <path key={d} d={d} />)}
			</g>

			<g fill={mix(tokens.color.warning, 22, tokens.fill.secondary)}>
				{BUILDINGS.map(([x, y, w, h]) => <rect key={`${x}-${y}`} x={x} y={y} width={w} height={h} rx={2} />)}
			</g>

			<rect x={148} y={100} width={66} height={42} rx={3} fill={mix(tokens.color.primary, 30, 'transparent')} stroke={tokens.color.primary} strokeWidth={2} />
			<path d="M181 56c-9 0-15 7-15 15 0 11 15 24 15 24s15-13 15-24c0-8-6-15-15-15z" fill={tokens.color.primary} />
			<circle cx={181} cy={71} r={5} fill={base} />

			<g fontSize={10} fill={tokens.text.default}>
				<rect x={14} y={176} width={70} height={4} fill={tokens.text.default} />
				<rect x={14} y={176} width={35} height={4} fill={base} />
				<text x={14} y={172}>0</text>
				<text x={70} y={172}>50 m</text>
			</g>
		</svg>
	);
}

export function FreePlanIllustration(props: { unit: string; }) {
	const tokens = useDesignTokens();
	const base = mix(tokens.color.primary, 4, tokens.surface.card);
	const gridId = React.useId();
	const [width, height, item] = PLAN_DIMENSIONS[props.unit] ?? PLAN_DIMENSIONS.cm!;

	return (
		<svg viewBox="0 14 320 172" width="100%" style={{ display: 'block', aspectRatio: '320 / 172' }} aria-hidden>
			<defs>
				<pattern id={gridId} width={16} height={16} patternUnits="userSpaceOnUse">
					<path d="M16 0H0V16" fill="none" stroke={mix(tokens.color.primary, 18, 'transparent')} strokeWidth={1} />
				</pattern>
			</defs>

			<rect width="320" height="200" fill={base} />
			<rect width="320" height="200" fill={`url(#${gridId})`} />

			<g fill="none" stroke={tokens.text.default} strokeWidth={5}>
				<path d="M64 48 H272 V160 H64 Z" />
				<path d="M176 48 V92" />
				<path d="M176 120 V160" />
				<path d="M64 112 H120" />
				<path d="M144 112 H176" />
			</g>

			<path d="M176 92 A28 28 0 0 1 204 120" fill="none" stroke={tokens.text.default} strokeWidth={1.2} strokeDasharray="3 3" />

			<g fill={mix(tokens.color.primary, 25, 'transparent')} stroke={tokens.color.primary} strokeWidth={1.5}>
				<rect x={200} y={70} width={48} height={24} rx={2} />
				<rect x={84} y={128} width={26} height={20} rx={2} />
			</g>

			<g stroke={tokens.color.primary} strokeWidth={1.2} fill="none">
				<path d="M64 28 H272" />
				<path d="M64 22 V34" />
				<path d="M272 22 V34" />
				<path d="M292 48 V160" />
				<path d="M286 48 H298" />
				<path d="M286 160 H298" />
			</g>

			<g fontSize={11} fill={tokens.color.primary} textAnchor="middle">
				<rect x={128} y={20} width={80} height={16} fill={base} />
				<text x={168} y={32}>{width}</text>

				<g transform="translate(296 104) rotate(-90)">
					<rect x={-34} y={-12} width={68} height={16} fill={base} />
					<text x={0} y={0}>{height}</text>
				</g>

				<text x={224} y={108} fontSize={9}>{item}</text>
			</g>
		</svg>
	);
}

const PLAN_DIMENSIONS: Partial<Record<string, [string, string, string]>> = {
	mm: ['13 000 mm', '7 000 mm', '1 200 mm'],
	cm: ['1 300 cm', '700 cm', '120 cm'],
	m: ['13 m', '7 m', '1,2 m'],
};
